// assets/mmm-warenkorb.js – rendert den Warenkorb neu und führt Aktionen über Venues Cart-Store aus.
// Fasst niemals Kauf-Buttons, Formulare oder fetch der Produktseite an.
// Venue-APIs (geprüft 14.09. an der Theme-Kopie):
//  - cart-store: add(items), change({id, quantity}), update({updates|attributes}, opId), refresh(),
//    resetVariantsBeingAdded(variantId?), getState(); jede Aktion feuert on:cart:loading/loaded/failed.
//  - modal-dialog: open()/close() am Element #modal-cart; a11y-dialog lauscht per document-Capture auf
//    [data-a11y-dialog-hide] → Schließen-Button funktioniert auch nach DOM-Tausch ohne eigenen Handler.
import Cart from 'cart-store';

const ROOT = '[data-mmm-warenkorb]';
const CART_URL = `${window.Shopify?.routes?.root ?? '/'}cart`;
let lastLoaded = 0;
let renderTimer = null;
let seq = 0;
let pendingBtn = null;
let lastType = '';
let renderFailed = false;

const root = () => document.querySelector(ROOT);
const modal = () => document.getElementById('modal-cart');

async function render() {
  const el = root();
  if (!el) return;
  const my = ++seq;
  const id = el.dataset.sectionId;
  const focused = document.activeElement && el.contains(document.activeElement) ? document.activeElement : null;
  // Drawer: Shop-Root (Produkt-URLs liefern bei Accept: JSON das Produkt-JSON statt der Sections, geprüft 14.09.);
  // /cart-Seite: eigener Pfad, weil die Template-Section nur dort existiert.
  const base = el.classList.contains('mmm-wk--page') ? location.pathname : (window.Shopify?.routes?.root ?? '/');
  const res = await fetch(`${base}?sections=${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`section ${res.status}`);
  const html = (await res.json())[id];
  if (my !== seq) return;                       // veraltete Antwort verwerfen
  const fresh = new DOMParser().parseFromString(html || '', 'text/html').querySelector(ROOT);
  if (!fresh) throw new Error('section empty');
  const current = root();
  if (!current) return;
  current.replaceWith(fresh);
  renderFailed = false;
  if (focused) restoreFocus(fresh, focused);
  markPreview();
  fitOffers();
  cleanupMonster();
  uhr();
}
// Fokus nach dem Tausch: gleicher Mengen-Button (nach Zeilen-key + Richtung, data-mmm-to ändert sich),
// sonst gleiches Angebot, sonst Schließen (Drawer) bzw. erstes fokussierbares Element (/cart)
function restoreFocus(fresh, old) {
  const esc = (v) => (window.CSS?.escape ? CSS.escape(v) : v);
  const label = old.getAttribute('aria-label');
  const target =
    (old.dataset.mmmQty && (fresh.querySelector(`[data-mmm-qty="${esc(old.dataset.mmmQty)}"][aria-label="${esc(label || '')}"]`)
      || fresh.querySelector(`[data-mmm-qty="${esc(old.dataset.mmmQty)}"]`)))
    || (old.dataset.mmmAdd && fresh.querySelector(`[data-mmm-add="${esc(old.dataset.mmmAdd)}"]`))
    || fresh.querySelector('.mmm-wk__close')
    || fresh.querySelector('button:not([disabled]), a[href]');
  target?.focus();
}

function setLoading(on) {
  const el = root();
  if (!el) return;
  el.classList.toggle('is-loading', on);
  if (on) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
}

let waiting = [];
function queueRender() {
  clearTimeout(renderTimer);
  const done = new Promise((resolve) => waiting.push(resolve));   // entprellte Aufrufe warten gemeinsam
  renderTimer = setTimeout(() => {
    const resolvers = waiting; waiting = [];
    render().catch(() => {
      renderFailed = true;
      setLoading(false);
      if (lastType === 'ADD' && !document.querySelector('#modal-cart .mmm-wk__item')) location.href = CART_URL;
    }).finally(() => resolvers.forEach(r => r()));
  }, 40);
  return done;
}

// Ein-Bildschirm-Regel: reicht der Platz nicht, zuerst Angebote von 2 auf 1 (nur messbar, wenn sichtbar)
// Theme-Vorschau (nicht veröffentlicht): Fuß bekommt Platz über der Shopify-Vorschauleiste
function markPreview() {
  const el = root();
  if (el && window.Shopify?.theme?.role !== 'main') el.classList.add('mmm-wk--preview');
}

function fitOffers() {
  const body = root()?.querySelector('[data-mmm-body]');
  if (!body || !body.clientHeight) return;
  const offers = body.querySelectorAll('.mmm-wk__offers > section');
  if (offers.length > 1 && body.scrollHeight > body.clientHeight + 1) offers[offers.length - 1].hidden = true;
}

let cleaned = false;
async function cleanupMonster() {
  const keys = root()?.dataset.mmmCleanup;
  if (cleaned || !keys) return;
  cleaned = true;                                // genau einmal pro Seitenaufruf, verhindert Render-Schleife
  try {
    const attributes = Object.fromEntries(JSON.parse(keys).map(k => [k, null]));
    await Cart.update({ attributes }, opId('mmm-cleanup'));
  } catch { /* still */ }
}

// Speicher-Uhr im Kopf (Jerome 23.10.2026): 2 Stunden ab dem ersten Artikel bzw. ab jedem neu hinzugefügten Artikel,
// läuft beim Neuladen weiter (localStorage), springt nie zurück; danach nur noch „Gespeichert ✓“ (kein 0:00, nichts verfällt).
const UHR_KEY = 'mmm_wk_uhr', UHR_MS = 2 * 3600 * 1000;
let uhrTakt = null;
function uhrStand() {
  const n = Number(root()?.dataset.mmmCount || 0);
  let st = null;
  try { st = JSON.parse(localStorage.getItem(UHR_KEY) || 'null'); } catch { /* privat */ }
  if (!n) { try { localStorage.removeItem(UHR_KEY); } catch {} return null; }
  if (!st || !st.s || n > (st.n || 0)) st = { s: Date.now(), n };
  else if (n < st.n) st.n = n;
  try { localStorage.setItem(UHR_KEY, JSON.stringify(st)); } catch { /* ohne Speicher: Uhr läuft nur bis zum Neuladen */ }
  return st;
}
function uhr() {
  clearInterval(uhrTakt);
  const el = root()?.querySelector('[data-mmm-uhr]');
  const st = uhrStand();
  if (!el || !st) return;
  const b = el.querySelector('b');
  const tick = () => {
    const rest = st.s + UHR_MS - Date.now();
    if (rest <= 0) { b.textContent = '✓'; clearInterval(uhrTakt); return; }
    const t = Math.ceil(rest / 1000), h = Math.floor(t / 3600), m = Math.floor(t % 3600 / 60), sec = t % 60;
    b.textContent = `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };
  tick(); el.hidden = false;
  uhrTakt = setInterval(tick, 1000);
}

// Vorschau groß: Motiv antippen → Vollbild, Tippen/Esc schließt
function zoom(src) {
  const o = document.createElement('div');
  o.className = 'mmm-wk-zoom'; o.setAttribute('role', 'dialog'); o.setAttribute('aria-label', 'Vorschau');
  const img = new Image(); img.src = src; img.alt = 'Vorschau deines Motivs';
  const x = document.createElement('button'); x.type = 'button'; x.setAttribute('aria-label', 'Vorschau schließen'); x.textContent = '×';
  const zu = () => { o.remove(); removeEventListener('keydown', esc); };
  const esc = (e) => { if (e.key === 'Escape') zu(); };
  o.append(img, x); o.addEventListener('click', zu); addEventListener('keydown', esc);
  document.body.append(o); x.focus();
}

// Toast außerhalb des neu gerenderten Abschnitts, damit er den Tausch überlebt
function toast(text) {
  const host = document.querySelector('#modal-cart .modal__wrapper') || document.body;
  const t = document.createElement('div');
  t.className = 'mmm-wk-toast'; t.setAttribute('role', 'status'); t.textContent = text;
  host.append(t); setTimeout(() => t.remove(), 2200);
}

const opId = (name) => `${name}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function toFormShape(id, quantity, props) {
  const item = { id: String(id), quantity: String(quantity) };
  for (const [k, v] of Object.entries(props || {})) item[`properties[${k}]`] = v;
  return item;
}

function showError(btn) {
  btn.closest('section')?.querySelector('.mmm-wk__err')?.removeAttribute('hidden');
  btn.disabled = false;
}

// Veralteten Inhalt nie ungekennzeichnet zeigen: ab on:cart:loading gedimmt + Ladebalken, bis neu gerendert
document.addEventListener('on:cart:loading', () => setLoading(true));
document.addEventListener('on:cart:loaded', (e) => { lastLoaded = Date.now(); lastType = e.detail?.type || ''; queueRender(); });
document.addEventListener('on:cart:failed', () => {
  if (pendingBtn) { showError(pendingBtn); pendingBtn = null; }
  queueRender();   // Store hat den Warenkorb neu geladen; Render entfernt auch den Ladezustand
});

// Vertrag 6.7: eigene Kaufbox öffnet den Warenkorb per Event → Store neu laden (Header-Zähler, on:cart:loaded),
// neu rendern, dann Venue-Modal öffnen. Kaufboxen mit eigenem fetch('/cart/add.js') brauchen den refresh.
document.addEventListener('mmm:warenkorb:oeffnen', async () => {
  try { await Cart.refresh(); } catch { /* Render unten entscheidet über Rückfall */ }
  await queueRender();
  const m = modal();
  if (!m || renderFailed) { location.href = CART_URL; return; }
  if (!m.shown) await m.open?.();
  fitOffers();
});

// Header-Symbol: ist das letzte Neu-Rendern gescheitert, normal zur /cart-Seite navigieren (Spez. 7).
// window-Capture läuft vor a11y-dialogs document-Capture → Öffnen des veralteten Drawers wird verhindert.
window.addEventListener('click', (e) => {
  if (renderFailed && e.target.closest?.('[data-a11y-dialog-show="modal-cart"]')) {
    e.preventDefault();
    e.stopImmediatePropagation();
    location.href = CART_URL;
  }
}, true);

modal()?.addEventListener('on:modal:opened', () => fitOffers());   // Event bubbelt nicht → direkt am Modal

document.addEventListener('click', async (e) => {
  const el = e.target.closest?.(`${ROOT} button`);
  if (!el || el.matches('[data-a11y-dialog-hide]')) return;   // Schließen erledigt a11y-dialog
  const wk = root();

  if (el.hasAttribute('data-mmm-zoom')) {
    const img = el.querySelector('img');
    if (img) zoom(img.currentSrc || img.src);
    return;
  }

  if (el.dataset.mmmColor) {
    const offer = el.closest('.mmm-wk__offer');
    offer.querySelector('[data-mmm-add]').dataset.mmmAdd = el.dataset.mmmColor;
    offer.querySelectorAll('[data-mmm-color]').forEach(b => b.setAttribute('aria-pressed', String(b === el)));
    offer.querySelector('.mmm-wk__framed')?.classList.toggle('is-white', el.classList.contains('is-white'));
    return;
  }
  if (el.dataset.mmmAdd) {
    const id = el.dataset.mmmAdd;
    el.disabled = true;
    pendingBtn = el;
    let props = {};
    try { props = JSON.parse(el.dataset.mmmProps || '{}'); } catch { /* ohne Merkmale */ }
    const qty = Number(el.dataset.mmmQtyAdd || 1);
    try {
      await Cart.add(toFormShape(id, qty, props));
    } catch {
      showError(el);
    } finally {
      pendingBtn = null;
      await Cart.resetVariantsBeingAdded(String(id)).catch(() => {});
    }
    return;
  }
  if (el.dataset.mmmRemove) {
    let list = [];
    try { list = JSON.parse(el.dataset.mmmRemove); } catch { return; }
    const updates = Object.fromEntries(list.map(k => [k, 0]));
    el.disabled = true;
    try {
      await Cart.update({ updates }, opId('mmm-remove'));
      if (list.length > 1) toast('Zugehörige Zusätze wurden mit entfernt');
    } catch { el.disabled = false; }
    return;
  }
  if (el.dataset.mmmQty) {
    el.disabled = true;
    await Cart.change({ id: el.dataset.mmmQty, quantity: Number(el.dataset.mmmTo) }).catch(() => { el.disabled = false; });
    return;
  }
  if (el.hasAttribute('data-mmm-more')) {
    wk.querySelectorAll('.mmm-wk__item.is-more').forEach(i => i.classList.remove('is-more'));
    el.remove();
  }
});

// Rückfall: Hinzufügungen außerhalb des Stores (z. B. App-eigene /cart/add.js-Aufrufe) – nur beobachten.
if ('PerformanceObserver' in window) {
  let t = null;
  try {
    new PerformanceObserver((list) => {
      if (!list.getEntries().some(en => /\/cart\/add(\.js)?(\?|$)/.test(en.name))) return;
      clearTimeout(t);
      t = setTimeout(() => {
        const busy = (Cart.getState().productVariantsBeingAdded || []).length > 0;
        if (!busy && Date.now() - lastLoaded > 1500) Cart.refresh().catch(() => {});
      }, 300);
    }).observe({ type: 'resource', buffered: false });
  } catch { /* ältere Browser */ }
}

// Motivbild bricht (z. B. TeeInBlue-Bild direkt nach dem Hinzufügen noch nicht auf dem CDN):
// einmal nach 2,5 s neu laden, danach Produktbild aus data-fallback. error bubbelt nicht → Capture-Phase.
document.addEventListener('error', (e) => {
  const img = e.target;
  if (!(img instanceof HTMLImageElement) || !img.dataset.fallback || !img.closest(ROOT)) return;
  if (!img.dataset.mmmRetried) {
    img.dataset.mmmRetried = '1';
    setTimeout(() => {
      if (!img.isConnected) return;
      try {
        const u = new URL(img.getAttribute('src'), location.href);
        u.searchParams.set('r', '1');
        img.src = u.href;
      } catch {
        img.src = img.dataset.fallback;   // ungültige Bild-URL → gleich Produktbild
      }
    }, 2500);
  } else if (img.getAttribute('src') !== img.dataset.fallback) {
    img.src = img.dataset.fallback;
  }
}, true);
// Bilder, die schon vor dem Laden dieses Moduls gescheitert sind (error-Event verpasst)
document.querySelectorAll(`${ROOT} img[data-fallback]`).forEach((img) => {
  if (img.complete && img.naturalWidth === 0 && img.getAttribute('src')) img.dispatchEvent(new Event('error'));
});

markPreview();
fitOffers();
cleanupMonster();
uhr();

// I2: Kaufboxen (Wandkunst, Tassen) öffnen den Drawer nur, wenn dieses Modul vollständig geladen ist – sonst /cart.
window.mmmWarenkorbBereit = true;
